/**
 * AdminTestData — Test-Daten-Manager
 *
 * 4-Tab-Übersicht (Konten / Projekte / Telesales / Audit-Log) zum
 * kontrollierten Bulk-Löschen von Test-Items. Backend filtert immer noch
 * mal die Schutzliste durch (Demo-Standard 001-004, Türkei-1, Stealth-Admins).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Eraser, RefreshCw, ShieldAlert, Trash2, Loader2, Users2, FolderKanban,
  Phone, ScrollText, CheckSquare, Square,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TABS = [
  { key: "users", label: "Test-Konten", icon: Users2 },
  { key: "projects", label: "Test-Projekte", icon: FolderKanban },
  { key: "telesales", label: "Test-Submissions", icon: Phone },
  { key: "audit", label: "Audit-Log", icon: ScrollText },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" }); }
  catch { return iso; }
};

export default function AdminTestData() {
  const [tab, setTab] = useState("users");
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({ users: new Set(), projects: new Set(), telesales: new Set() });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [audit, setAudit] = useState({ entries: [], loading: false });

  const loadScan = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/test-data/scan");
      setScan(r.data);
      setSelected({ users: new Set(), projects: new Set(), telesales: new Set() });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan fehlgeschlagen");
    } finally { setLoading(false); }
  }, []);

  const loadAudit = useCallback(async () => {
    setAudit((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get("/admin/audit-log?limit=100");
      const onlyTest = (r.data.entries || []).filter(
        (e) => e.action?.startsWith("test_data.") || e.target_type === "test_data",
      );
      setAudit({ entries: onlyTest, loading: false });
    } catch {
      setAudit({ entries: [], loading: false });
    }
  }, []);

  useEffect(() => { loadScan(); }, [loadScan]);
  useReloadBus("*", loadScan);
  useEffect(() => { if (tab === "audit") loadAudit(); }, [tab, loadAudit]);

  const items = useMemo(() => {
    if (!scan || tab === "audit") return [];
    return scan[tab] || [];
  }, [scan, tab]);

  const sel = selected[tab] || new Set();

  const toggle = (id) => {
    setSelected((prev) => {
      const next = { ...prev };
      const s = new Set(next[tab]);
      if (s.has(id)) s.delete(id); else s.add(id);
      next[tab] = s;
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      const next = { ...prev };
      next[tab] = sel.size === items.length ? new Set() : new Set(items.map((i) => i.id));
      return next;
    });
  };

  const doDelete = async () => {
    if (!sel.size) return;
    setDeleting(true);
    try {
      const r = await api.post("/admin/test-data/delete", {
        kind: tab,
        ids: Array.from(sel),
      });
      const d = r.data;
      const msg = `${d.deleted} gelöscht${d.skipped?.length ? ` · ${d.skipped.length} übersprungen` : ""}`;
      if (d.deleted > 0) toast.success(msg);
      else toast.warning(msg);
      if (d.skipped?.length) {
        console.warn("[test-data] skipped:", d.skipped);
      }
      setConfirmOpen(false);
      await loadScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setDeleting(false); }
  };

  const renderHeader = (
    <>
      <AppHeader title="Test-Daten-Manager" back="/admin/users" />
      <div className="max-w-6xl mx-auto px-3 sm:px-5 pt-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-2xl bg-red-500/15 flex items-center justify-center">
            <Eraser className="text-red-300" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Test-Daten-Manager</h1>
            <p className="text-zinc-400 text-[12px] mt-0.5 font-medium">
              Kontrolliertes Aufräumen von Test-Konten, Test-Projekten und Test-Submissions.
            </p>
          </div>
          <button
            data-testid="test-data-refresh"
            onClick={loadScan}
            disabled={loading}
            className="shrink-0 h-10 w-10 rounded-xl bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 flex items-center justify-center"
            title="Erneut scannen"
          >
            <RefreshCw size={14} className={loading ? "animate-spin text-red-300" : "text-zinc-400"} />
          </button>
        </div>

        {/* Protected hint */}
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-[12px] text-amber-200 mb-4 flex items-start gap-2 font-medium" data-testid="protected-hint">
          <ShieldAlert size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>Geschützt (NIE löschbar):</strong> Demo-Standard 001–004
            (<span className="font-mono">+491700000001–04</span>) · Türkei-1 Telesales-Demo
            (<span className="font-mono">+4915255001000/002001/002002</span>) · Stealth-/Super-Admins.
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="test-data-tabs">
          {TABS.map((t) => {
            const count = t.key === "audit"
              ? audit.entries.length
              : scan?.totals?.[t.key];
            const isActive = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-bold border transition ${
                  isActive
                    ? "bg-red-500/15 text-red-200 border-red-500/40"
                    : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-800"
                }`}
              >
                <Icon size={13} />
                {t.label}
                {count != null && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded text-[12px] font-mono ${isActive ? "bg-red-500/30 text-red-100" : "bg-zinc-800 text-zinc-400"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-test-data">
      {renderHeader}
      <main className="max-w-6xl mx-auto px-3 sm:px-5 pb-10">
        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin inline text-zinc-500" /></div>
        ) : tab === "audit" ? (
          <AuditView audit={audit} />
        ) : (
          <ListView
            tab={tab}
            items={items}
            sel={sel}
            toggle={toggle}
            toggleAll={toggleAll}
            onDeleteClick={() => sel.size > 0 && setConfirmOpen(true)}
          />
        )}
      </main>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="confirm-delete-dialog" className="bg-[#18181B] border-red-500/40">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-200">
              <Trash2 size={18} /> {sel.size} {TABS.find((t) => t.key === tab)?.label} endgültig löschen?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300 text-[13px]">
              Diese Aktion ist <strong>irreversibel</strong>. Das Backend prüft nochmals die Schutzliste und überspringt geschützte Items automatisch. Alle abhängigen Daten (Projekte, Offers, Photos, Tasks, …) werden mit-gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel" disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete"
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? <><Loader2 className="animate-spin inline mr-1.5" size={13} />Lösche…</> : `${sel.size} löschen`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function ListView({ tab, items, sel, toggle, toggleAll, onDeleteClick }) {
  if (!items.length) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-10 text-center" data-testid={`empty-${tab}`}>
        <p className="text-zinc-400 text-[13px]">Keine Test-Daten gefunden. 🎉</p>
      </div>
    );
  }

  return (
    <div data-testid={`list-${tab}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <button
          data-testid="select-all-btn"
          onClick={toggleAll}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 text-[12px] font-bold hover:bg-zinc-800"
        >
          {sel.size === items.length ? <CheckSquare size={13} /> : <Square size={13} />}
          {sel.size === items.length ? "Alle abwählen" : `Alle (${items.length}) wählen`}
        </button>
        <button
          data-testid="bulk-delete-btn"
          onClick={onDeleteClick}
          disabled={!sel.size}
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-bold border transition ${
            sel.size
              ? "bg-red-600 text-white border-red-700 hover:bg-red-700"
              : "bg-zinc-900 text-zinc-600 border-zinc-800 cursor-not-allowed"
          }`}
        >
          <Trash2 size={13} /> {sel.size} löschen
        </button>
      </div>

      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-[#0c0c0e] text-[12px] uppercase tracking-widest text-zinc-300 font-semibold">
            <tr>
              <th className="py-2.5 px-3 w-10"></th>
              {tab === "users" && (<>
                <th className="py-2.5 px-3 text-left">Name</th>
                <th className="py-2.5 px-3 text-left hidden sm:table-cell">E-Mail</th>
                <th className="py-2.5 px-3 text-left hidden md:table-cell">Telefon</th>
                <th className="py-2.5 px-3 text-left">Rolle</th>
                <th className="py-2.5 px-3 text-right hidden sm:table-cell">Angelegt</th>
              </>)}
              {tab === "projects" && (<>
                <th className="py-2.5 px-3 text-left">Projektnummer</th>
                <th className="py-2.5 px-3 text-left">Kunde</th>
                <th className="py-2.5 px-3 text-left hidden sm:table-cell">Source</th>
                <th className="py-2.5 px-3 text-left">Status</th>
                <th className="py-2.5 px-3 text-right hidden sm:table-cell">Angelegt</th>
              </>)}
              {tab === "telesales" && (<>
                <th className="py-2.5 px-3 text-left">Name</th>
                <th className="py-2.5 px-3 text-left hidden sm:table-cell">E-Mail / Telefon</th>
                <th className="py-2.5 px-3 text-left hidden md:table-cell">Projektnummer</th>
                <th className="py-2.5 px-3 text-left">Status</th>
                <th className="py-2.5 px-3 text-right hidden sm:table-cell">Erfasst</th>
              </>)}
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const checked = sel.has(it.id);
              return (
                <tr
                  key={it.id}
                  data-testid={`row-${tab}-${it.id}`}
                  className={`border-t border-zinc-800 hover:bg-zinc-900/40 ${checked ? "bg-red-500/5" : ""}`}
                >
                  <td className="py-2.5 px-3 align-top">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(it.id)}
                      data-testid={`check-${tab}-${it.id}`}
                      className="h-4 w-4 accent-red-500"
                    />
                  </td>
                  {tab === "users" && (<>
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-zinc-100">{it.name || "—"}</div>
                      {it.is_test_account && <span className="text-[12px] text-amber-400">is_test_account</span>}
                    </td>
                    <td className="py-2.5 px-3 hidden sm:table-cell text-zinc-400">{it.email || "—"}</td>
                    <td className="py-2.5 px-3 hidden md:table-cell text-zinc-400 font-mono text-[12px]">{it.phone || "—"}</td>
                    <td className="py-2.5 px-3 text-zinc-300">{it.role}</td>
                    <td className="py-2.5 px-3 hidden sm:table-cell text-right text-zinc-500">{fmtDate(it.created_at)}</td>
                  </>)}
                  {tab === "projects" && (<>
                    <td className="py-2.5 px-3 font-mono font-bold text-zinc-100">{it.project_number || "—"}</td>
                    <td className="py-2.5 px-3 text-zinc-300">{it.customer_name}</td>
                    <td className="py-2.5 px-3 hidden sm:table-cell text-zinc-400">{it.source || "—"}</td>
                    <td className="py-2.5 px-3 text-zinc-300">{it.status || "—"}</td>
                    <td className="py-2.5 px-3 hidden sm:table-cell text-right text-zinc-500">{fmtDate(it.created_at)}</td>
                  </>)}
                  {tab === "telesales" && (<>
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-zinc-100">{it.name || "—"}</div>
                      <div className="text-[10.5px] text-zinc-500">{it.zip} {it.city}</div>
                    </td>
                    <td className="py-2.5 px-3 hidden sm:table-cell">
                      <div className="text-zinc-400 truncate max-w-[200px]">{it.email || "—"}</div>
                      <div className="text-zinc-300 font-mono text-[12px]">{it.phone || ""}</div>
                    </td>
                    <td className="py-2.5 px-3 hidden md:table-cell font-mono text-[12px] text-zinc-300">{it.project_number || "—"}</td>
                    <td className="py-2.5 px-3 text-zinc-300">{it.status || "—"}</td>
                    <td className="py-2.5 px-3 hidden sm:table-cell text-right text-zinc-500">{fmtDate(it.created_at)}</td>
                  </>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function AuditView({ audit }) {
  if (audit.loading) {
    return <div className="text-center py-16"><Loader2 className="animate-spin inline text-zinc-500" /></div>;
  }
  if (!audit.entries.length) {
    return (
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-10 text-center" data-testid="empty-audit">
        <p className="text-zinc-400 text-[13px]">Noch keine Test-Daten-Löschungen protokolliert.</p>
      </div>
    );
  }
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden" data-testid="audit-list">
      <table className="w-full text-[12.5px]">
        <thead className="bg-[#0c0c0e] text-[12px] uppercase tracking-widest text-zinc-300 font-semibold">
          <tr>
            <th className="py-2.5 px-3 text-left">Zeit</th>
            <th className="py-2.5 px-3 text-left">Aktor</th>
            <th className="py-2.5 px-3 text-left">Aktion</th>
            <th className="py-2.5 px-3 text-right">Gelöscht</th>
            <th className="py-2.5 px-3 text-right hidden sm:table-cell">Übersprungen</th>
          </tr>
        </thead>
        <tbody>
          {audit.entries.map((e) => (
            <tr key={e.id} className="border-t border-zinc-800" data-testid={`audit-row-${e.id}`}>
              <td className="py-2.5 px-3 text-zinc-400 whitespace-nowrap">{fmtDate(e.ts)}</td>
              <td className="py-2.5 px-3 text-zinc-300">
                <div className="font-bold">{e.actor_name || "—"}</div>
                <div className="text-[10.5px] text-zinc-500">{e.actor_role}</div>
              </td>
              <td className="py-2.5 px-3 font-mono text-[12px] text-red-200">{e.action}</td>
              <td className="py-2.5 px-3 text-right font-mono font-bold text-[#00E575]">{e.metadata?.deleted ?? 0}</td>
              <td className="py-2.5 px-3 text-right hidden sm:table-cell font-mono text-amber-300">{e.metadata?.skipped?.length ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
